import React, { useState, useEffect } from "react";
import { get, post, put } from "../../../services/api";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  CircularProgress,
} from "@mui/material";
import { toast } from "react-toastify";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./EmployeeProfile.css";

function EmployeeProfile() {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const employeeId = searchParams.get("employeeId");
  const mode = searchParams.get("mode") || "view";

  const [activeTab, setActiveTab] = useState("personal");
  const [photo, setPhoto] = useState("https://via.placeholder.com/160");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const API_BASE_URL = process.env.REACT_APP_LIVE_DOMAIN_URL;
  const savedCenterId =
    localStorage.getItem("centerId") === "null"
      ? null
      : localStorage.getItem("centerId");
  const organizationId = localStorage.getItem("organizationId");

  // Master Data
  const [departments, setDepartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [allSubCategories, setAllSubCategories] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [positions, setPositions] = useState([]);
  const [contractTypes, setContractTypes] = useState([]);
  const [employeeStatuses, setEmployeeStatuses] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loanTypes, setLoanTypes] = useState([]);
  const [payElements, setPayElements] = useState([]);
  const [salaryHistory, setSalaryHistory] = useState([]);
  // Employee Data

  const [expandedLoan, setExpandedLoan] = useState(null);

  const [employeeData, setEmployeeData] = useState({
    code: "",
    fullName: "",
    cnic: "",
    dateOfBirth: "",
    gender: "Male",
    primaryPhone: "",
    primaryEmail: "",
    address: "",
    employeeStatusId: "",
  });

  // Job Details
  const [jobDetails, setJobDetails] = useState({
    departmentId: "",
    categoryId: "",
    subCategoryId: "",
    designationId: "",
    contractTypeId: "",
    joiningDate: "",
    workEmail: "",
  });

  // Banking Details
  const [bankingDetails, setBankingDetails] = useState({
    bankAccountTitle: "",
    bankName: "",
    iban: "",
    branch: "",
  });

  // Tax Details
  const [taxDetails, setTaxDetails] = useState({
    ntn: "",
    eobi: "",
    socialSecurity: "",
  });

  // Position Details
  const [positionForm, setPositionForm] = useState({
    positionId: "",
    designationId: "",
    gradeLevel: "",
    salaryGross: "",
    reportingManagerId: "",
    location: "",
    reason: "",
    effectiveFrom: "",
  });

  // Current Job
  const [currentJob, setCurrentJob] = useState(null);

  // Salary
  const [currentSalary, setCurrentSalary] = useState(null);
  const [salaryForm, setSalaryForm] = useState({
    basicAmount: 0,
    effectiveFrom: "",
  });

  // Allowances & Deductions
  const [allowances, setAllowances] = useState([]);
  const [deductions, setDeductions] = useState([]);

  // Leave & Loan
  const [leaveForm, setLeaveForm] = useState({
    leaveTypeId: "",
    startDate: "",
    endDate: "",
    reason: "",
    status: "Pending", // ✅ ADD
    approverName: "", // ✅ ADD
  });

  const [loanForm, setLoanForm] = useState({
    loanTypeId: "",
    principalAmount: "",
    interestRate: 0,
    tenureMonths: 12,
    startMonth: "", // ✅ ADD (format: YYYY-MM)
    installmentDay: 5, // ✅ ADD
    purpose: "",
  });

  // Lists
  const [leaves, setLeaves] = useState([]);
  const [loans, setLoans] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [employmentHistory, setEmploymentHistory] = useState([]);
  const [positionHistory, setPositionHistory] = useState([]);

  // Document Upload
  const [selectedFile, setSelectedFile] = useState(null);
  const [documentType, setDocumentType] = useState("CNIC");

  // Pagination
  const [leavePage, setLeavePage] = useState(1);
  const [loanPage, setLoanPage] = useState(1);
  const itemsPerPage = 5;

  useEffect(() => {
    loadMasterData();
    if (employeeId && mode === "view") {
      loadEmployeeData();
    } else if (mode === "new") {
      generateEmployeeCode();
    }
  }, [employeeId, mode]);

  const generateEmployeeCode = async () => {
    try {
      const response = await get("Employee/next-code?organizationId=1", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.success) {
        setEmployeeData((prev) => ({ ...prev, code: response.data }));
      }
    } catch (error) {
      console.error("Error generating code:", error);
      setEmployeeData((prev) => ({ ...prev, code: "EMP-0001" }));
    }
  };

  const loadMasterData = async () => {
    try {
      const [
        depts,
        cats,
        subcats,
        desigs,
        pos,
        contracts,
        statuses,
        leaveTypesRes,
        loanTypesRes,
        payElems,
      ] = await Promise.all([
        get(
          `Configuration/departments?organizationId=${organizationId}&CenterId=${savedCenterId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        ),
        get(
          `Configuration/categories?organizationId=${organizationId}&CenterId=${savedCenterId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        ),
        get(
          `Configuration/subcategories?organizationId=${organizationId}&CenterId=${savedCenterId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        ),
        get(
          `Configuration/designations?organizationId=${organizationId}&CenterId=${savedCenterId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        ),
        get(
          `Configuration/positions?organizationId=${organizationId}&CenterId=${savedCenterId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        ),
        get(
          `Configuration/contract-types?organizationId=${organizationId}&CenterId=${savedCenterId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        ),
        get(
          `Configuration/employee-statuses?organizationId=${organizationId}&CenterId=${savedCenterId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        ),
        get(
          `Configuration/leave-types?organizationId=${organizationId}&CenterId=${savedCenterId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        ),
        get(
          `Configuration/loan-types?organizationId=${organizationId}&CenterId=${savedCenterId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        ),
        get(
          `Configuration/pay-elements?organizationId=${organizationId}&CenterId=${savedCenterId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        ),
      ]);

      setDepartments(depts.data || []);
      setCategories(cats.data || []);
      setAllSubCategories(subcats.data || []);
      setDesignations(desigs.data || []);
      setPositions(pos.data || []);
      setContractTypes(contracts.data || []);
      setEmployeeStatuses(statuses.data || []);
      setLeaveTypes(leaveTypesRes.data || []);
      setLoanTypes(loanTypesRes.data || []);
      setPayElements(payElems.data || []);
    } catch (error) {
      console.error("Error loading master data:", error);
      toast.error("Failed to load master data");
    }
  };

  const handleCategoryChange = (categoryId) => {
    setJobDetails({ ...jobDetails, categoryId, subCategoryId: "" });
    if (categoryId) {
      const filtered = allSubCategories.filter(
        (sc) => sc.categoryId == categoryId,
      );
      setSubCategories(filtered);
    } else {
      setSubCategories([]);
    }
  };

  const loadEmployeeData = async () => {
    try {
      setLoading(true);
      const response = await get(`Employee/${employeeId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.success) {
        const emp = response.data;
        setEmployeeData({
          code: emp.code || "",
          fullName: emp.fullName || "",
          cnic: emp.cnic || "",
          dateOfBirth: emp.dateOfBirth ? emp.dateOfBirth.split("T")[0] : "",
          gender: emp.gender || "Male",
          primaryPhone: emp.primaryPhone || "",
          primaryEmail: emp.primaryEmail || "",
          address: emp.address || "",
          employeeStatusId: emp.employeeStatusId || "",
        });

        await loadEmployeeDetails(employeeId);
      }
    } catch (error) {
      console.error("Error loading employee:", error);
      toast.error("Failed to load employee data");
    } finally {
      setLoading(false);
    }
  };

  const loadEmployeeDetails = async (empId) => {
    try {
      // Load Job
      try {
        const jobRes = await get(`Employee/${empId}/current-job`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (jobRes.success && jobRes.data) {
          setCurrentJob(jobRes.data);
          setJobDetails({
            departmentId: jobRes.data.departmentId || "",
            categoryId: jobRes.data.categoryId || "",
            subCategoryId: jobRes.data.subCategoryId || "",
            designationId: jobRes.data.designationId || "",
            contractTypeId: jobRes.data.contractTypeId || "",
            joiningDate: jobRes.data.joiningDate
              ? jobRes.data.joiningDate.split("T")[0]
              : "",
            workEmail: jobRes.data.workEmail || "",
          });

          if (jobRes.data.categoryId) {
            const filtered = allSubCategories.filter(
              (sc) => sc.categoryId == jobRes.data.categoryId,
            );
            setSubCategories(filtered);
          }
        }
      } catch (error) {
        console.log("No job details found");
      }

      // Load Salary History
      try {
        const salaryHistoryRes = await get(`Salary/employee/${empId}/history`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (salaryHistoryRes.success) {
          console.log("Salary History Loaded:", salaryHistoryRes.data);
          setSalaryHistory(salaryHistoryRes.data || []);
        }
      } catch (error) {
        console.log("No salary history found");
      }
      // Load Banking Details
      try {
        const bankRes = await get(`Employee/${empId}/bank-account`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (bankRes.success && bankRes.data) {
          setBankingDetails({
            bankAccountTitle: bankRes.data.accountTitle || "",
            bankName: bankRes.data.bankName || "",
            iban: bankRes.data.iban || "",
            branch: bankRes.data.branch || "",
          });
        }
      } catch (error) {
        console.log("No banking details found");
      }

      // Load Tax Details
      try {
        const taxRes = await get(`Employee/${empId}/tax-details`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (taxRes.success && taxRes.data) {
          setTaxDetails({
            ntn: taxRes.data.ntn || "",
            eobi: taxRes.data.eobi || "",
            socialSecurity: taxRes.data.socialSecurity || "",
          });
        }
      } catch (error) {
        console.log("No tax details found");
      }

      // Load Salary
      try {
        const salaryRes = await get(`Salary/employee/${empId}/current`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (salaryRes.success && salaryRes.data) {
          setCurrentSalary(salaryRes.data);

          // Load allowances & deductions
          setAllowances(
            (salaryRes.data.benefits || []).map((b) => ({
              element: b.payElementId,
              amount: b.amount,
              effectiveDate: b.effectiveDate
                ? b.effectiveDate.split("T")[0]
                : "",
              endDate: b.endDate ? b.endDate.split("T")[0] : "",
            })),
          );

          setDeductions(
            (salaryRes.data.deductions || []).map((d) => ({
              element: d.payElementId,
              amount: d.amount,
              effectiveDate: d.effectiveDate
                ? d.effectiveDate.split("T")[0]
                : "",
              endDate: d.endDate ? d.endDate.split("T")[0] : "",
            })),
          );
        }
      } catch (error) {
        console.log("No salary found");
      }

      // Load Leaves
      // Load Leaves
      try {
        console.log("Loading leaves for employee:", empId);
        const leavesRes = await get(`Leave/employee/${empId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log("Leaves Response:", leavesRes);
        if (leavesRes.success) {
          console.log("Leaves Data:", leavesRes.data);
          setLeaves(leavesRes.data || []);
        }
      } catch (error) {
        console.log("Error loading leaves:", error);
        setLeaves([]);
      }

      // Load Loans
      try {
        const loansRes = await get(`Loan/employee/${empId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (loansRes.success) setLoans(loansRes.data || []);
      } catch (error) {
        console.log("No loans found");
      }

      // Load Documents
      try {
        const docsRes = await get(`Employee/${empId}/documents`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (docsRes.success) setDocuments(docsRes.data || []);
      } catch (error) {
        console.log("No documents found");
      }

      // Load Position History
      // Load Position History
      try {
        const posHistoryRes = await get(`Employee/${empId}/position-history`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (posHistoryRes.success) {
          console.log("Position History Loaded:", posHistoryRes.data); // ✅ Add this log
          setPositionHistory(posHistoryRes.data || []);
        }
      } catch (error) {
        console.log("No position history found");
      }

      // Load Employment History
      try {
        const empHistoryRes = await get(
          `Employee/${empId}/employment-history`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        if (empHistoryRes.success)
          setEmploymentHistory(empHistoryRes.data || []);
      } catch (error) {
        console.log("No employment history found");
      }
    } catch (error) {
      console.error("Error loading employee details:", error);
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhoto(reader.result);
        toast.success("Photo updated");
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSavePersonal = async () => {
    if (
      !employeeData.code ||
      !employeeData.fullName ||
      !employeeData.primaryEmail
    ) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      setSaving(true);
      const payload = {
        code: employeeData.code,
        centerId: savedCenterId,
        organizationId: organizationId,
        fullName: employeeData.fullName,
        cnic: employeeData.cnic || null,
        dateOfBirth: employeeData.dateOfBirth || null,
        gender: employeeData.gender || null,
        primaryPhone: employeeData.primaryPhone,
        primaryEmail: employeeData.primaryEmail,
        address: employeeData.address || null,
        employeeStatusId: employeeData.employeeStatusId
          ? parseInt(employeeData.employeeStatusId)
          : null,
      };

      let response;
      if (mode === "new" || !employeeId) {
        response = await post("Employee", payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.success) {
          toast.success("Employee created successfully!");
          navigate(`/employee?mode=view&employeeId=${response.data.id}`);
        }
      } else {
        response = await post(`Employee/${employeeId}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.success) {
          toast.success("Personal information updated!");
        }
      }
    } catch (error) {
      console.error("Error saving employee:", error);
      toast.error(error.response?.data?.message || "Failed to save employee");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveJobDetails = async () => {
    if (!employeeId) {
      toast.error("Please save personal information first");
      return;
    }

    if (
      !jobDetails.departmentId ||
      !jobDetails.designationId ||
      !jobDetails.joiningDate
    ) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      setSaving(true);
      const response = await post(
        "Employee/job-assignment",
        {
          employeeId: parseInt(employeeId),
          departmentId: parseInt(jobDetails.departmentId),
          categoryId: jobDetails.categoryId
            ? parseInt(jobDetails.categoryId)
            : null,
          subCategoryId: jobDetails.subCategoryId
            ? parseInt(jobDetails.subCategoryId)
            : null,
          designationId: parseInt(jobDetails.designationId),
          contractTypeId: jobDetails.contractTypeId
            ? parseInt(jobDetails.contractTypeId)
            : null,
          joiningDate: jobDetails.joiningDate,
          workEmail: jobDetails.workEmail,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.success) {
        toast.success("Job details saved successfully!");
        await loadEmployeeDetails(employeeId);
      }
    } catch (error) {
      console.error("Error saving job details:", error);
      toast.error(
        error.response?.data?.message || "Failed to save job details",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBanking = async () => {
    if (!employeeId) {
      toast.error("Please save personal information first");
      return;
    }

    if (!bankingDetails.bankAccountTitle || !bankingDetails.iban) {
      toast.error("Please fill account title and IBAN");
      return;
    }

    try {
      setSaving(true);
      const response = await post(
        "Employee/bank-account",
        {
          employeeId: parseInt(employeeId),
          bankName: bankingDetails.bankName,
          accountTitle: bankingDetails.bankAccountTitle,
          iban: bankingDetails.iban,
          branch: bankingDetails.branch,
          isPrimary: true,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.success) {
        toast.success("Banking details saved successfully!");
        await loadEmployeeDetails(employeeId);
      }
    } catch (error) {
      console.error("Error saving banking details:", error);
      toast.error(
        error.response?.data?.message || "Failed to save banking details",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTaxDetails = async () => {
    if (!employeeId) {
      toast.error("Please save personal information first");
      return;
    }

    try {
      setSaving(true);
      const response = await post(
        "Employee/tax-details",
        {
          employeeId: parseInt(employeeId),
          ntn: taxDetails.ntn,
          eobi: taxDetails.eobi,
          socialSecurity: taxDetails.socialSecurity,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.success) {
        toast.success("Tax details saved successfully!");
      }
    } catch (error) {
      console.error("Error saving tax details:", error);
      toast.error(
        error.response?.data?.message || "Failed to save tax details",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleAssignPosition = async () => {
    if (!employeeId) {
      toast.error("Please save employee first");
      return;
    }

    if (!positionForm.effectiveFrom) {
      toast.error("Please fill effective date");
      return;
    }

    try {
      setSaving(true);
      const response = await post(
        "Employee/position-assignment",
        {
          employeeId: parseInt(employeeId),
          positionId: positionForm.positionId
            ? parseInt(positionForm.positionId)
            : null,
          designationId: positionForm.designationId
            ? parseInt(positionForm.designationId)
            : null,
          gradeLevel: positionForm.gradeLevel,
          salaryGross: positionForm.salaryGross
            ? parseFloat(positionForm.salaryGross)
            : null,
          reportingManagerId: positionForm.reportingManagerId
            ? parseInt(positionForm.reportingManagerId)
            : null,
          location: positionForm.location,
          reason: positionForm.reason,
          effectiveFrom: positionForm.effectiveFrom,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.success) {
        toast.success("Position assigned successfully!");
        setPositionForm({
          positionId: "",
          designationId: "",
          gradeLevel: "",
          salaryGross: "",
          reportingManagerId: "",
          location: "",
          reason: "",
          effectiveFrom: "",
        });
        await loadEmployeeDetails(employeeId);
      }
    } catch (error) {
      console.error("Error assigning position:", error);
      toast.error(error.response?.data?.message || "Failed to assign position");
    } finally {
      setSaving(false);
    }
  };

  const handleApplyLeave = async () => {
    if (!employeeId) {
      toast.error("Please save employee first");
      return;
    }

    if (!leaveForm.leaveTypeId || !leaveForm.startDate || !leaveForm.endDate) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      setSaving(true);

      const start = new Date(leaveForm.startDate);
      const end = new Date(leaveForm.endDate);
      const totalDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;

      const response = await post(
        "Leave",
        {
          employeeId: parseInt(employeeId),
          leaveTypeId: parseInt(leaveForm.leaveTypeId),
          fromDate: leaveForm.startDate,
          toDate: leaveForm.endDate,
          totalDays: totalDays,
          reason: leaveForm.reason,
          status: leaveForm.status, // ✅ ADD
          approverName: leaveForm.approverName, // ✅ ADD
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.success) {
        toast.success("Leave applied successfully!");
        setLeaveForm({
          leaveTypeId: "",
          startDate: "",
          endDate: "",
          reason: "",
          status: "Pending",
          approverName: "",
        });
        loadEmployeeDetails(employeeId);
      }
    } catch (error) {
      console.error("Error applying leave:", error);
      toast.error(error.response?.data?.message || "Failed to apply leave");
    } finally {
      setSaving(false);
    }
  };

  const handleApplyLoan = async () => {
    if (!employeeId) {
      toast.error("Please save employee first");
      return;
    }

    if (
      !loanForm.loanTypeId ||
      !loanForm.principalAmount ||
      !loanForm.startMonth
    ) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      setSaving(true);

      // Convert YYYY-MM to date (first day of month)
      const startDate = `${loanForm.startMonth}-${String(loanForm.installmentDay).padStart(2, "0")}`; // ✅ Changed padLeft to padStart

      const response = await post(
        "Loan",
        {
          employeeId: parseInt(employeeId),
          loanTypeId: parseInt(loanForm.loanTypeId),
          principalAmount: parseFloat(loanForm.principalAmount),
          interestRate: parseFloat(loanForm.interestRate) || 0,
          tenureMonths: parseInt(loanForm.tenureMonths),
          startDate: startDate,
          installmentDay: parseInt(loanForm.installmentDay),
          remarks: loanForm.purpose,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.success) {
        toast.success("Loan applied successfully!");
        setLoanForm({
          loanTypeId: "",
          principalAmount: "",
          interestRate: 0,
          tenureMonths: 12,
          startMonth: "",
          installmentDay: 5,
          purpose: "",
        });
        loadEmployeeDetails(employeeId);
      }
    } catch (error) {
      console.error("Error applying loan:", error);
      toast.error(error.response?.data?.message || "Failed to apply loan");
    } finally {
      setSaving(false);
    }
  };

  const handleAssignSalary = async () => {
    if (!employeeId) {
      toast.error("Please save employee first");
      return;
    }

    if (!salaryForm.basicAmount || !salaryForm.effectiveFrom) {
      toast.error("Please fill basic salary and effective date");
      return;
    }

    try {
      setSaving(true);

      // Prepare components (allowances + deductions)
      const components = [];

      allowances.forEach((item) => {
        if (item.element && item.amount && item.effectiveDate) {
          // ✅ Find the element name
          const payElement = payElements.find(
            (pe) => pe.id === parseInt(item.element),
          );

          components.push({
            payElementId: parseInt(item.element),
            element: payElement?.name || "", // ✅ ADD ELEMENT NAME
            amount: parseFloat(item.amount),
            effectiveDate: item.effectiveDate,
            endDate: item.endDate || null,
          });
        }
      });

      deductions.forEach((item) => {
        if (item.element && item.amount && item.effectiveDate) {
          // ✅ Find the element name
          const payElement = payElements.find(
            (pe) => pe.id === parseInt(item.element),
          );

          components.push({
            payElementId: parseInt(item.element),
            element: payElement?.name || "", // ✅ ADD ELEMENT NAME
            amount: parseFloat(item.amount),
            effectiveDate: item.effectiveDate,
            endDate: item.endDate || null,
          });
        }
      });

      const payload = {
        employeeId: parseInt(employeeId),
        basicAmount: parseFloat(salaryForm.basicAmount),
        effectiveFrom: salaryForm.effectiveFrom,
        components: components,
      };

      console.log("=== SALARY PAYLOAD ===", JSON.stringify(payload, null, 2));

      const response = await post("Salary/employee-salary", payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.success) {
        toast.success("Salary assigned successfully!");
        setSalaryForm({ basicAmount: 0, effectiveFrom: "" });
        setAllowances([]);
        setDeductions([]);
        loadEmployeeDetails(employeeId);
      }
    } catch (error) {
      console.error("Error assigning salary:", error);
      toast.error(error.response?.data?.message || "Failed to assign salary");
    } finally {
      setSaving(false);
    }
  };
  const handleFileSelect = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleDocumentUpload = async () => {
    if (!employeeId) {
      toast.error("Please save employee first");
      return;
    }

    if (!selectedFile) {
      toast.error("Please select a file");
      return;
    }

    try {
      setSaving(true);
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("employeeId", employeeId);
      formData.append("documentType", documentType);

      const response = await post("Employee/documents/upload", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.success) {
        toast.success("Document uploaded successfully!");
        setSelectedFile(null);
        document.getElementById("fileInput").value = "";
        await loadEmployeeDetails(employeeId);
      }
    } catch (error) {
      console.error("Error uploading document:", error);
      toast.error(error.response?.data?.message || "Failed to upload document");
    } finally {
      setSaving(false);
    }
  };

  // Allowance/Deduction Management
  const addAllowance = () => {
    setAllowances([
      ...allowances,
      { element: "", amount: 0, effectiveDate: "", endDate: "" },
    ]);
  };

  const addDeduction = () => {
    setDeductions([
      ...deductions,
      { element: "", amount: 0, effectiveDate: "", endDate: "" },
    ]);
  };

  const removeAllowance = (index) => {
    setAllowances(allowances.filter((_, i) => i !== index));
  };

  const removeDeduction = (index) => {
    setDeductions(deductions.filter((_, i) => i !== index));
  };

  const paginateData = (data, page) => {
    const start = (page - 1) * itemsPerPage;
    return data.slice(start, start + itemsPerPage);
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "400px" }}
      >
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className="row g-3">
      <div className="col-md-12">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4>{mode === "new" ? "Create New Employee" : "Employee Profile"}</h4>
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => navigate("/employee-list")}
            >
              <i className="fas fa-arrow-left me-1"></i> Back to List
            </button>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="row g-3 mb-4">
              <div className="col-md-3">
                <div className="text-center border rounded p-3">
                  <div className="position-relative d-inline-block mb-3">
                    <img
                      src={photo}
                      alt="Employee"
                      style={{
                        width: "160px",
                        height: "160px",
                        borderRadius: "16px",
                        border: "1px dashed #e5e7eb",
                        objectFit: "cover",
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn-light btn-sm position-absolute"
                      style={{ right: "6px", bottom: "6px" }}
                      onClick={() =>
                        document.getElementById("photoInput").click()
                      }
                    >
                      Change
                    </button>
                    <input
                      type="file"
                      id="photoInput"
                      hidden
                      accept="image/*"
                      onChange={handlePhotoChange}
                    />
                  </div>
                  <h5 className="mb-1">
                    {employeeData.fullName || "New Employee"}
                  </h5>
                  <div className="text-muted small mb-3">
                    Code: {employeeData.code || "N/A"}
                  </div>
                  {currentJob && (
                    <>
                      <hr />
                      <div className="text-start small">
                        <div className="mb-2">
                          <span className="text-muted d-block">Department</span>
                          <span>{currentJob.departmentName}</span>
                        </div>
                        <div className="mb-2">
                          <span className="text-muted d-block">
                            Designation
                          </span>
                          <span>{currentJob.designationName}</span>
                        </div>
                        <div className="mb-2">
                          <span className="text-muted d-block">Status</span>
                          <span className="badge bg-success">
                            {employeeStatuses.find(
                              (s) =>
                                s.id ===
                                parseInt(employeeData.employeeStatusId),
                            )?.name || "N/A"}
                          </span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="col-md-9">
                <ul className="nav nav-pills mb-3">
                  <li className="nav-item">
                    <button
                      className={`nav-link ${
                        activeTab === "personal" ? "active" : ""
                      }`}
                      onClick={() => setActiveTab("personal")}
                      type="button"
                    >
                      Personal
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === "job" ? "active" : ""}`}
                      onClick={() => setActiveTab("job")}
                      type="button"
                    >
                      Job Details
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${
                        activeTab === "positions" ? "active" : ""
                      }`}
                      onClick={() => setActiveTab("positions")}
                      type="button"
                    >
                      Positions
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${
                        activeTab === "banking" ? "active" : ""
                      }`}
                      onClick={() => setActiveTab("banking")}
                      type="button"
                    >
                      Banking
                    </button>
                  </li>
                  {/* <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === "tax" ? "active" : ""}`}
                      onClick={() => setActiveTab("tax")}
                      type="button"
                    >
                      Tax & Compliance
                    </button>
                  </li> */}
                  <li className="nav-item">
                    <button
                      className={`nav-link ${
                        activeTab === "documents" ? "active" : ""
                      }`}
                      onClick={() => setActiveTab("documents")}
                      type="button"
                    >
                      Documents
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${
                        activeTab === "salary" ? "active" : ""
                      }`}
                      onClick={() => setActiveTab("salary")}
                      type="button"
                    >
                      Salary
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${
                        activeTab === "leaves" ? "active" : ""
                      }`}
                      onClick={() => setActiveTab("leaves")}
                      type="button"
                    >
                      Leaves
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === "loans" ? "active" : ""}`}
                      onClick={() => setActiveTab("loans")}
                      type="button"
                    >
                      Loans
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${
                        activeTab === "history" ? "active" : ""
                      }`}
                      onClick={() => setActiveTab("history")}
                      type="button"
                    >
                      Employment History
                    </button>
                  </li>
                </ul>

                {/* Personal Tab */}
                {activeTab === "personal" && (
                  <div>
                    <h5 className="mb-3 fs-9 border-bottom pb-2">
                      Personal Information
                    </h5>
                    <div className="row g-3">
                      <div className="col-md-4">
                        <label className="form-label">
                          Employee Code<span className="text-danger">*</span>
                        </label>
                        <input
                          className="form-control"
                          value={employeeData.code}
                          onChange={(e) =>
                            setEmployeeData({
                              ...employeeData,
                              code: e.target.value,
                            })
                          }
                          disabled={mode === "view"}
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">
                          Full Name<span className="text-danger">*</span>
                        </label>
                        <input
                          className="form-control"
                          value={employeeData.fullName}
                          onChange={(e) =>
                            setEmployeeData({
                              ...employeeData,
                              fullName: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">CNIC</label>
                        <input
                          className="form-control"
                          value={employeeData.cnic}
                          onChange={(e) =>
                            setEmployeeData({
                              ...employeeData,
                              cnic: e.target.value,
                            })
                          }
                          placeholder="42101-1234567-1"
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Date of Birth</label>
                        <input
                          type="date"
                          className="form-control"
                          value={employeeData.dateOfBirth}
                          onChange={(e) =>
                            setEmployeeData({
                              ...employeeData,
                              dateOfBirth: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Gender</label>
                        <select
                          className="form-control"
                          value={employeeData.gender}
                          onChange={(e) =>
                            setEmployeeData({
                              ...employeeData,
                              gender: e.target.value,
                            })
                          }
                        >
                          <option>Male</option>
                          <option>Female</option>
                          <option>Other</option>
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">
                          Primary Phone<span className="text-danger">*</span>
                        </label>
                        <input
                          className="form-control"
                          value={employeeData.primaryPhone}
                          onChange={(e) =>
                            setEmployeeData({
                              ...employeeData,
                              primaryPhone: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">
                          Primary Email<span className="text-danger">*</span>
                        </label>
                        <input
                          type="email"
                          className="form-control"
                          value={employeeData.primaryEmail}
                          onChange={(e) =>
                            setEmployeeData({
                              ...employeeData,
                              primaryEmail: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Employee Status</label>
                        <select
                          className="form-control"
                          value={employeeData.employeeStatusId}
                          onChange={(e) =>
                            setEmployeeData({
                              ...employeeData,
                              employeeStatusId: e.target.value,
                            })
                          }
                        >
                          <option value="">-- Select Status --</option>
                          {employeeStatuses.map((status) => (
                            <option key={status.id} value={status.id}>
                              {status.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-12">
                        <label className="form-label">Address</label>
                        <textarea
                          className="form-control"
                          rows="2"
                          value={employeeData.address}
                          onChange={(e) =>
                            setEmployeeData({
                              ...employeeData,
                              address: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>

                    <div className="mt-4">
                      <button
                        type="button"
                        className="btn btn-success"
                        onClick={handleSavePersonal}
                        disabled={saving}
                      >
                        <i className="fas fa-save me-1"></i>{" "}
                        {saving ? "Saving..." : "Save Personal Information"}
                      </button>
                    </div>
                  </div>
                )}

                {/* Job Details Tab */}
                {activeTab === "job" && (
                  <div>
                    <h5 className="mb-3 fs-9 border-bottom pb-2">
                      Job Details
                    </h5>

                    {!employeeId && (
                      <div className="alert alert-warning mb-3">
                        <i className="fas fa-info-circle me-2"></i>
                        Please save personal information first
                      </div>
                    )}

                    <div className="row g-3">
                      <div className="col-md-4">
                        <label className="form-label">
                          Department<span className="text-danger">*</span>
                        </label>
                        <select
                          className="form-control"
                          value={jobDetails.departmentId}
                          onChange={(e) =>
                            setJobDetails({
                              ...jobDetails,
                              departmentId: e.target.value,
                            })
                          }
                          disabled={!employeeId}
                        >
                          <option value="">-- Select Department --</option>
                          {departments.map((dept) => (
                            <option key={dept.id} value={dept.id}>
                              {dept.departmentName || dept.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Category</label>
                        <select
                          className="form-control"
                          value={jobDetails.categoryId}
                          onChange={(e) => handleCategoryChange(e.target.value)}
                          disabled={!employeeId}
                        >
                          <option value="">-- Select Category --</option>
                          {categories.map((cat) => (
                            <option key={cat.id} value={cat.id}>
                              {cat.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Sub Category</label>
                        <select
                          className="form-control"
                          value={jobDetails.subCategoryId}
                          onChange={(e) =>
                            setJobDetails({
                              ...jobDetails,
                              subCategoryId: e.target.value,
                            })
                          }
                          disabled={!employeeId || !jobDetails.categoryId}
                        >
                          <option value="">-- Select Sub Category --</option>
                          {subCategories.map((sc) => (
                            <option key={sc.id} value={sc.id}>
                              {sc.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">
                          Designation<span className="text-danger">*</span>
                        </label>
                        <select
                          className="form-control"
                          value={jobDetails.designationId}
                          onChange={(e) =>
                            setJobDetails({
                              ...jobDetails,
                              designationId: e.target.value,
                            })
                          }
                          disabled={!employeeId}
                        >
                          <option value="">-- Select Designation --</option>
                          {designations.map((desig) => (
                            <option key={desig.id} value={desig.id}>
                              {desig.title || desig.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Contract Type</label>
                        <select
                          className="form-control"
                          value={jobDetails.contractTypeId}
                          onChange={(e) =>
                            setJobDetails({
                              ...jobDetails,
                              contractTypeId: e.target.value,
                            })
                          }
                          disabled={!employeeId}
                        >
                          <option value="">-- Select Contract Type --</option>
                          {contractTypes.map((ct) => (
                            <option key={ct.id} value={ct.id}>
                              {ct.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">
                          Joining Date<span className="text-danger">*</span>
                        </label>
                        <input
                          type="date"
                          className="form-control"
                          value={jobDetails.joiningDate}
                          onChange={(e) =>
                            setJobDetails({
                              ...jobDetails,
                              joiningDate: e.target.value,
                            })
                          }
                          disabled={!employeeId}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Work Email</label>
                        <input
                          type="email"
                          className="form-control"
                          value={jobDetails.workEmail}
                          onChange={(e) =>
                            setJobDetails({
                              ...jobDetails,
                              workEmail: e.target.value,
                            })
                          }
                          disabled={!employeeId}
                        />
                      </div>
                    </div>

                    <div className="mt-4">
                      <button
                        type="button"
                        className="btn btn-success"
                        onClick={handleSaveJobDetails}
                        disabled={saving || !employeeId}
                      >
                        <i className="fas fa-save me-1"></i>{" "}
                        {saving ? "Saving..." : "Save Job Details"}
                      </button>
                    </div>
                  </div>
                )}

                {/* Positions Tab */}
                {activeTab === "positions" && (
                  <div>
                    <h5 className="mb-3 fs-9 border-bottom pb-2">
                      Position Management
                    </h5>

                    {!employeeId && (
                      <div className="alert alert-warning mb-3">
                        <i className="fas fa-info-circle me-2"></i>
                        Please save personal information first
                      </div>
                    )}

                    <div className="row g-3 mb-4">
                      <div className="col-md-4">
                        <label className="form-label">Position</label>
                        <select
                          className="form-control"
                          value={positionForm.positionId}
                          onChange={(e) =>
                            setPositionForm({
                              ...positionForm,
                              positionId: e.target.value,
                            })
                          }
                          disabled={!employeeId}
                        >
                          <option value="">-- Select Position --</option>
                          {positions.map((pos) => (
                            <option key={pos.id} value={pos.id}>
                              {pos.title || pos.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Designation</label>
                        <select
                          className="form-control"
                          value={positionForm.designationId}
                          onChange={(e) =>
                            setPositionForm({
                              ...positionForm,
                              designationId: e.target.value,
                            })
                          }
                          disabled={!employeeId}
                        >
                          <option value="">-- Select Designation --</option>
                          {designations.map((desig) => (
                            <option key={desig.id} value={desig.id}>
                              {desig.title || desig.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">
                          Effective From<span className="text-danger">*</span>
                        </label>
                        <input
                          type="date"
                          className="form-control"
                          value={positionForm.effectiveFrom}
                          onChange={(e) =>
                            setPositionForm({
                              ...positionForm,
                              effectiveFrom: e.target.value,
                            })
                          }
                          disabled={!employeeId}
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Grade Level</label>
                        <input
                          className="form-control"
                          value={positionForm.gradeLevel}
                          onChange={(e) =>
                            setPositionForm({
                              ...positionForm,
                              gradeLevel: e.target.value,
                            })
                          }
                          disabled={!employeeId}
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Salary Gross</label>
                        <input
                          type="number"
                          className="form-control"
                          value={positionForm.salaryGross}
                          onChange={(e) =>
                            setPositionForm({
                              ...positionForm,
                              salaryGross: e.target.value,
                            })
                          }
                          disabled={!employeeId}
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Location</label>
                        <input
                          className="form-control"
                          value={positionForm.location}
                          onChange={(e) =>
                            setPositionForm({
                              ...positionForm,
                              location: e.target.value,
                            })
                          }
                          disabled={!employeeId}
                        />
                      </div>
                      <div className="col-md-3 d-flex align-items-end">
                        <button
                          className="btn btn-success w-100"
                          onClick={handleAssignPosition}
                          disabled={!employeeId || saving}
                        >
                          <i className="fas fa-check me-1"></i>
                          {saving ? "Saving..." : "Assign"}
                        </button>
                      </div>
                      <div className="col-12">
                        <label className="form-label">Reason</label>
                        <textarea
                          className="form-control"
                          rows="2"
                          value={positionForm.reason}
                          onChange={(e) =>
                            setPositionForm({
                              ...positionForm,
                              reason: e.target.value,
                            })
                          }
                          disabled={!employeeId}
                        />
                      </div>
                    </div>

                    <hr />
                    <h5 className="mb-3 fs-9">Position History</h5>
                    {positionHistory.length === 0 ? (
                      <div className="alert alert-info">
                        No position history found
                      </div>
                    ) : (
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: "#f3f4f6" }}>
                              <TableCell>Position</TableCell>
                              <TableCell>Designation</TableCell>
                              <TableCell>Effective From</TableCell>
                              <TableCell>Effective To</TableCell>
                              <TableCell>Reason</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {positionHistory.map((pos) => (
                              <TableRow key={pos.id} hover>
                                <TableCell>
                                  {pos.positionTitle || "N/A"}
                                </TableCell>
                                <TableCell>
                                  {pos.designationTitle || "N/A"}
                                </TableCell>
                                <TableCell>
                                  {pos.effectiveFrom
                                    ? pos.effectiveFrom.split("T")[0]
                                    : "N/A"}
                                </TableCell>
                                <TableCell>
                                  {pos.effectiveTo
                                    ? pos.effectiveTo.split("T")[0]
                                    : "Current"}
                                </TableCell>
                                <TableCell>{pos.reason || "-"}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </div>
                )}

                {/* Banking Tab */}
                {activeTab === "banking" && (
                  <div>
                    <h5 className="mb-3 fs-9 border-bottom pb-2">
                      Banking Information
                    </h5>

                    {!employeeId && (
                      <div className="alert alert-warning mb-3">
                        <i className="fas fa-info-circle me-2"></i>
                        Please save personal information first
                      </div>
                    )}

                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label">
                          Account Title<span className="text-danger">*</span>
                        </label>
                        <input
                          className="form-control"
                          value={bankingDetails.bankAccountTitle}
                          onChange={(e) =>
                            setBankingDetails({
                              ...bankingDetails,
                              bankAccountTitle: e.target.value,
                            })
                          }
                          disabled={!employeeId}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Bank Name</label>
                        <input
                          className="form-control"
                          value={bankingDetails.bankName}
                          onChange={(e) =>
                            setBankingDetails({
                              ...bankingDetails,
                              bankName: e.target.value,
                            })
                          }
                          disabled={!employeeId}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">
                          IBAN<span className="text-danger">*</span>
                        </label>
                        <input
                          className="form-control"
                          value={bankingDetails.iban}
                          onChange={(e) =>
                            setBankingDetails({
                              ...bankingDetails,
                              iban: e.target.value,
                            })
                          }
                          placeholder="PK36HABB0000000000000000"
                          disabled={!employeeId}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Branch</label>
                        <input
                          className="form-control"
                          value={bankingDetails.branch}
                          onChange={(e) =>
                            setBankingDetails({
                              ...bankingDetails,
                              branch: e.target.value,
                            })
                          }
                          disabled={!employeeId}
                        />
                      </div>
                    </div>

                    <div className="mt-4">
                      <button
                        type="button"
                        className="btn btn-success"
                        onClick={handleSaveBanking}
                        disabled={saving || !employeeId}
                      >
                        <i className="fas fa-save me-1"></i>{" "}
                        {saving ? "Saving..." : "Save Banking Details"}
                      </button>
                    </div>
                  </div>
                )}

                {/* Tax & Compliance Tab */}
                {/* {activeTab === "tax" && (
                  <div>
                    <h5 className="mb-3 fs-9 border-bottom pb-2">
                      Tax & Compliance
                    </h5>

                    {!employeeId && (
                      <div className="alert alert-warning mb-3">
                        <i className="fas fa-info-circle me-2"></i>
                        Please save personal information first
                      </div>
                    )}

                    <div className="row g-3">
                      <div className="col-md-4">
                        <label className="form-label">NTN</label>
                        <input
                          className="form-control"
                          value={taxDetails.ntn}
                          onChange={(e) =>
                            setTaxDetails({ ...taxDetails, ntn: e.target.value })
                          }
                          disabled={!employeeId}
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">EOBI #</label>
                        <input
                          className="form-control"
                          value={taxDetails.eobi}
                          onChange={(e) =>
                            setTaxDetails({ ...taxDetails, eobi: e.target.value })
                          }
                          disabled={!employeeId}
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Social Security #</label>
                        <input
                          className="form-control"
                          value={taxDetails.socialSecurity}
                          onChange={(e) =>
                            setTaxDetails({
                              ...taxDetails,
                              socialSecurity: e.target.value,
                            })
                          }
                          disabled={!employeeId}
                        />
                      </div>
                    </div>

                    <div className="mt-4">
                      <button
                        type="button"
                        className="btn btn-success"
                        onClick={handleSaveTaxDetails}
                        disabled={!employeeId || saving}
                      >
                        <i className="fas fa-save me-1"></i>{" "}
                        {saving ? "Saving..." : "Save Tax Details"}
                      </button>
                    </div>
                  </div>
                )} */}

                {/* Documents Tab */}
                {activeTab === "documents" && (
                  <div>
                    <h5 className="mb-3 fs-9 border-bottom pb-2">Documents</h5>

                    {!employeeId && (
                      <div className="alert alert-warning mb-3">
                        <i className="fas fa-info-circle me-2"></i>
                        Please save personal information first
                      </div>
                    )}

                    <div className="row g-3 mb-4">
                      <div className="col-md-4">
                        <label className="form-label">Document Type</label>
                        <select
                          className="form-control"
                          value={documentType}
                          onChange={(e) => setDocumentType(e.target.value)}
                          disabled={!employeeId}
                        >
                          <option>CNIC</option>
                          <option>Resume</option>
                          <option>Educational_Certificate</option>
                          <option>Appointment_Letter</option>
                          <option>Passport</option>
                          <option>Medical_Certificate</option>
                          <option>Other</option>
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Upload File</label>
                        <input
                          type="file"
                          id="fileInput"
                          className="form-control"
                          accept=".pdf,.jpg,.jpeg,.png,.docx"
                          onChange={handleFileSelect}
                          disabled={!employeeId}
                        />
                        <small className="text-muted">
                          Supported: PDF, JPG, PNG, DOCX (Max 10MB)
                        </small>
                      </div>
                      <div className="col-md-2 d-flex align-items-end">
                        <button
                          className="btn btn-primary w-100"
                          onClick={handleDocumentUpload}
                          disabled={!employeeId || !selectedFile || saving}
                        >
                          <i className="fas fa-upload me-1"></i>
                          {saving ? "Uploading..." : "Upload"}
                        </button>
                      </div>
                    </div>

                    <hr />

                    <h6 className="mb-3">
                      <i className="fas fa-file-alt me-2"></i>Uploaded Documents
                      ({documents.length})
                    </h6>

                    {documents.length === 0 ? (
                      <div className="alert alert-info">
                        <i className="fas fa-info-circle me-2"></i>
                        No documents uploaded yet
                      </div>
                    ) : (
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: "#f3f4f6" }}>
                              <TableCell>
                                <i className="fas fa-folder-open me-2"></i>
                                Document Type
                              </TableCell>
                              <TableCell>
                                <i className="fas fa-file me-2"></i>File Name
                              </TableCell>
                              <TableCell>
                                <i className="fas fa-calendar me-2"></i>Uploaded
                                Date
                              </TableCell>
                              <TableCell align="center">
                                <i className="fas fa-tasks me-2"></i>Actions
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {documents.map((doc) => (
                              <TableRow key={doc.id} hover>
                                <TableCell>
                                  <span className="badge bg-primary">
                                    {doc.documentType}
                                  </span>
                                </TableCell>
                                <TableCell>
                                  <i className="fas fa-file-pdf me-2 text-danger"></i>
                                  {doc.fileName || doc.remarks || "Document"}
                                </TableCell>
                                <TableCell>
                                  {doc.uploadedDate
                                    ? new Date(
                                        doc.uploadedDate,
                                      ).toLocaleDateString("en-US", {
                                        year: "numeric",
                                        month: "short",
                                        day: "numeric",
                                      })
                                    : doc.createdAt
                                      ? new Date(
                                          doc.createdAt,
                                        ).toLocaleDateString("en-US", {
                                          year: "numeric",
                                          month: "short",
                                          day: "numeric",
                                        })
                                      : "N/A"}
                                </TableCell>
                                <TableCell align="center">
                                  <a
                                    href={`${API_BASE_URL}${doc.filePath}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-sm btn-outline-primary me-2"
                                    title="View Document"
                                  >
                                    <i className="fas fa-eye"></i>
                                  </a>

                                  <a
                                    href={`${API_BASE_URL}${doc.filePath}`}
                                    download
                                    className="btn btn-sm btn-outline-success"
                                    title="Download Document"
                                  >
                                    <i className="fas fa-download"></i>
                                  </a>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </div>
                )}

                {/* Salary Tab with Allowances & Deductions + HISTORY */}
                {activeTab === "salary" && (
                  <div>
                    <h5 className="mb-3 fs-9 border-bottom pb-2">
                      Salary Management
                    </h5>

                    {!employeeId && (
                      <div className="alert alert-warning mb-3">
                        <i className="fas fa-info-circle me-2"></i>
                        Please save personal information first
                      </div>
                    )}

                    {currentSalary && currentSalary.basicSalary > 0 && (
                      <div className="alert alert-success mb-3">
                        <div className="row">
                          <div className="col-md-3">
                            <strong>Current Basic:</strong>
                            <br />
                            Rs.{" "}
                            {(currentSalary.basicSalary || 0).toLocaleString()}
                          </div>
                          <div className="col-md-3">
                            <strong>Total Benefits:</strong>
                            <br />
                            Rs.{" "}
                            {(
                              currentSalary.benefits?.reduce(
                                (sum, b) => sum + (b.amount || 0),
                                0,
                              ) || 0
                            ).toLocaleString()}
                          </div>
                          <div className="col-md-3">
                            <strong>Total Deductions:</strong>
                            <br />
                            Rs.{" "}
                            {(
                              currentSalary.deductions?.reduce(
                                (sum, d) => sum + (d.amount || 0),
                                0,
                              ) || 0
                            ).toLocaleString()}
                          </div>
                          <div className="col-md-3">
                            <strong>Net Salary:</strong>
                            <br />
                            <span className="text-white fw-bold">
                              Rs.{" "}
                              {(
                                (currentSalary.basicSalary || 0) +
                                (currentSalary.benefits?.reduce(
                                  (sum, b) => sum + (b.amount || 0),
                                  0,
                                ) || 0) -
                                (currentSalary.deductions?.reduce(
                                  (sum, d) => sum + (d.amount || 0),
                                  0,
                                ) || 0)
                              ).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="card mb-4">
                      <div className="card-header bg-light">
                        <h6 className="mb-0">
                          <i className="fas fa-plus-circle me-2"></i>Assign New
                          Salary
                        </h6>
                      </div>
                      <div className="card-body">
                        <div className="row g-3 mb-4">
                          <div className="col-md-4">
                            <label className="form-label">
                              Basic Salary<span className="text-danger">*</span>
                            </label>
                            <input
                              type="number"
                              className="form-control"
                              value={salaryForm.basicAmount}
                              onChange={(e) =>
                                setSalaryForm({
                                  ...salaryForm,
                                  basicAmount: e.target.value,
                                })
                              }
                              disabled={!employeeId}
                              placeholder="0.00"
                            />
                          </div>
                          <div className="col-md-4">
                            <label className="form-label">
                              Effective From
                              <span className="text-danger">*</span>
                            </label>
                            <input
                              type="date"
                              className="form-control"
                              value={salaryForm.effectiveFrom}
                              onChange={(e) =>
                                setSalaryForm({
                                  ...salaryForm,
                                  effectiveFrom: e.target.value,
                                })
                              }
                              disabled={!employeeId}
                            />
                          </div>
                          <div className="col-md-4 d-flex align-items-end">
                            <button
                              className="btn btn-success w-100"
                              onClick={handleAssignSalary}
                              disabled={!employeeId || saving}
                            >
                              <i className="fas fa-check me-1"></i>
                              {saving ? "Saving..." : "Assign Salary"}
                            </button>
                          </div>
                        </div>

                        <hr />

                        {/* Benefit & Deduction Elements */}
                        <div className="row g-4">
                          <div className="col-lg-6">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <div className="fw-semibold">
                                <i className="fas fa-plus-square me-2 text-success"></i>
                                Benefit Elements
                              </div>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-success"
                                onClick={addAllowance}
                                disabled={!employeeId}
                              >
                                <i className="fas fa-plus me-1"></i>Add
                              </button>
                            </div>
                            <div className="table-responsive">
                              <table className="table table-sm table-bordered align-middle">
                                <thead className="table-light">
                                  <tr>
                                    <th style={{ width: "40%" }}>Element</th>
                                    <th
                                      style={{ width: "20%" }}
                                      className="text-end"
                                    >
                                      Amount
                                    </th>
                                    <th style={{ width: "20%" }}>Effective</th>
                                    <th style={{ width: "15%" }}>End Date</th>
                                    <th style={{ width: "5%" }}></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {allowances.length === 0 ? (
                                    <tr>
                                      <td
                                        colSpan="5"
                                        className="text-center text-muted"
                                      >
                                        No benefits added
                                      </td>
                                    </tr>
                                  ) : (
                                    allowances.map((item, idx) => (
                                      <tr key={idx}>
                                        <td>
                                          <select
                                            className="form-select form-select-sm"
                                            value={item.element}
                                            onChange={(e) => {
                                              const updated = [...allowances];
                                              updated[idx].element =
                                                e.target.value;
                                              setAllowances(updated);
                                            }}
                                          >
                                            <option value="">Select</option>
                                            {payElements
                                              .filter(
                                                (pe) =>
                                                  pe.elementType === "Benefit",
                                              )
                                              .map((pe) => (
                                                <option
                                                  key={pe.id}
                                                  value={pe.id}
                                                >
                                                  {pe.name}
                                                </option>
                                              ))}
                                          </select>
                                        </td>
                                        <td>
                                          <input
                                            className="form-control form-control-sm text-end"
                                            type="number"
                                            value={item.amount}
                                            onChange={(e) => {
                                              const updated = [...allowances];
                                              updated[idx].amount =
                                                e.target.value;
                                              setAllowances(updated);
                                            }}
                                          />
                                        </td>
                                        <td>
                                          <input
                                            type="date"
                                            className="form-control form-control-sm"
                                            value={item.effectiveDate}
                                            onChange={(e) => {
                                              const updated = [...allowances];
                                              updated[idx].effectiveDate =
                                                e.target.value;
                                              setAllowances(updated);
                                            }}
                                          />
                                        </td>
                                        <td>
                                          <input
                                            type="date"
                                            className="form-control form-control-sm"
                                            value={item.endDate}
                                            onChange={(e) => {
                                              const updated = [...allowances];
                                              updated[idx].endDate =
                                                e.target.value;
                                              setAllowances(updated);
                                            }}
                                          />
                                        </td>
                                        <td>
                                          <button
                                            className="btn btn-sm btn-link text-danger px-1"
                                            type="button"
                                            onClick={() => removeAllowance(idx)}
                                          >
                                            ✕
                                          </button>
                                        </td>
                                      </tr>
                                    ))
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>

                          {/* Deduction Elements */}
                          <div className="col-lg-6">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <div className="fw-semibold">
                                <i className="fas fa-minus-square me-2 text-danger"></i>
                                Deduction Elements
                              </div>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={addDeduction}
                                disabled={!employeeId}
                              >
                                <i className="fas fa-plus me-1"></i>Add
                              </button>
                            </div>
                            <div className="table-responsive">
                              <table className="table table-sm table-bordered align-middle">
                                <thead className="table-light">
                                  <tr>
                                    <th style={{ width: "40%" }}>Element</th>
                                    <th
                                      style={{ width: "20%" }}
                                      className="text-end"
                                    >
                                      Amount
                                    </th>
                                    <th style={{ width: "20%" }}>Effective</th>
                                    <th style={{ width: "15%" }}>End Date</th>
                                    <th style={{ width: "5%" }}></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {deductions.length === 0 ? (
                                    <tr>
                                      <td
                                        colSpan="5"
                                        className="text-center text-muted"
                                      >
                                        No deductions added
                                      </td>
                                    </tr>
                                  ) : (
                                    deductions.map((item, idx) => (
                                      <tr key={idx}>
                                        <td>
                                          <select
                                            className="form-select form-select-sm"
                                            value={item.element}
                                            onChange={(e) => {
                                              const updated = [...deductions];
                                              updated[idx].element =
                                                e.target.value;
                                              setDeductions(updated);
                                            }}
                                          >
                                            <option value="">Select</option>
                                            {payElements
                                              .filter(
                                                (pe) =>
                                                  pe.elementType ===
                                                  "Deduction",
                                              )
                                              .map((pe) => (
                                                <option
                                                  key={pe.id}
                                                  value={pe.id}
                                                >
                                                  {pe.name}
                                                </option>
                                              ))}
                                          </select>
                                        </td>
                                        <td>
                                          <input
                                            className="form-control form-control-sm text-end"
                                            type="number"
                                            value={item.amount}
                                            onChange={(e) => {
                                              const updated = [...deductions];
                                              updated[idx].amount =
                                                e.target.value;
                                              setDeductions(updated);
                                            }}
                                          />
                                        </td>
                                        <td>
                                          <input
                                            type="date"
                                            className="form-control form-control-sm"
                                            value={item.effectiveDate}
                                            onChange={(e) => {
                                              const updated = [...deductions];
                                              updated[idx].effectiveDate =
                                                e.target.value;
                                              setDeductions(updated);
                                            }}
                                          />
                                        </td>
                                        <td>
                                          <input
                                            type="date"
                                            className="form-control form-control-sm"
                                            value={item.endDate}
                                            onChange={(e) => {
                                              const updated = [...deductions];
                                              updated[idx].endDate =
                                                e.target.value;
                                              setDeductions(updated);
                                            }}
                                          />
                                        </td>
                                        <td>
                                          <button
                                            className="btn btn-sm btn-link text-danger px-1"
                                            type="button"
                                            onClick={() => removeDeduction(idx)}
                                          >
                                            ✕
                                          </button>
                                        </td>
                                      </tr>
                                    ))
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ✅ SALARY HISTORY TABLE */}
                    <hr />
                    <h5 className="mb-3 fs-9">
                      <i className="fas fa-history me-2"></i>Salary History
                    </h5>
                    {salaryHistory.length === 0 ? (
                      <div className="alert alert-info">
                        <i className="fas fa-info-circle me-2"></i>
                        No salary history found
                      </div>
                    ) : (
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: "#f3f4f6" }}>
                              <TableCell>Effective From</TableCell>
                              <TableCell>Effective To</TableCell>
                              <TableCell align="right">Basic Salary</TableCell>
                              <TableCell align="right">
                                Total Benefits
                              </TableCell>
                              <TableCell align="right">
                                Total Deductions
                              </TableCell>
                              <TableCell align="right">
                                <strong>Net Salary</strong>
                              </TableCell>
                              <TableCell align="center">Details</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {salaryHistory.map((history) => (
                              <TableRow key={history.id} hover>
                                <TableCell>
                                  {history.effectiveFrom
                                    ? new Date(
                                        history.effectiveFrom,
                                      ).toLocaleDateString()
                                    : "N/A"}
                                </TableCell>
                                <TableCell>
                                  {history.effectiveTo ? (
                                    new Date(
                                      history.effectiveTo,
                                    ).toLocaleDateString()
                                  ) : (
                                    <span className="badge bg-success">
                                      Current
                                    </span>
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  Rs.{" "}
                                  {(history.basicSalary || 0).toLocaleString()}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  className="text-success"
                                >
                                  Rs.{" "}
                                  {(
                                    history.totalBenefits || 0
                                  ).toLocaleString()}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  className="text-danger"
                                >
                                  Rs.{" "}
                                  {(
                                    history.totalDeductions || 0
                                  ).toLocaleString()}
                                </TableCell>
                                <TableCell align="right">
                                  <strong>
                                    Rs.{" "}
                                    {(history.netSalary || 0).toLocaleString()}
                                  </strong>
                                </TableCell>
                                <TableCell align="center">
                                  <button
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => {
                                      // Show detailed breakdown in modal or expand
                                      alert(
                                        `Benefits: ${history.benefits.length}\nDeductions: ${history.deductions.length}`,
                                      );
                                    }}
                                  >
                                    <i className="fas fa-eye"></i>
                                  </button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </div>
                )}
                {/* Leaves Tab */}
                {activeTab === "leaves" && (
                  <div>
                    <h5 className="mb-3 fs-9 border-bottom pb-2">
                      Apply Leave
                    </h5>

                    {!employeeId && (
                      <div className="alert alert-warning mb-3">
                        <i className="fas fa-info-circle me-2"></i>
                        Please save personal information first
                      </div>
                    )}

                    <div className="row g-3 mb-4">
                      <div className="col-md-3">
                        <label className="form-label">
                          Leave Type<span className="text-danger">*</span>
                        </label>
                        <select
                          className="form-control"
                          value={leaveForm.leaveTypeId}
                          onChange={(e) =>
                            setLeaveForm({
                              ...leaveForm,
                              leaveTypeId: e.target.value,
                            })
                          }
                          disabled={!employeeId}
                        >
                          <option value="">-- Select Type --</option>
                          {leaveTypes.map((lt) => (
                            <option key={lt.id} value={lt.id}>
                              {lt.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-2">
                        <label className="form-label">
                          Start Date<span className="text-danger">*</span>
                        </label>
                        <input
                          type="date"
                          className="form-control"
                          value={leaveForm.startDate}
                          onChange={(e) =>
                            setLeaveForm({
                              ...leaveForm,
                              startDate: e.target.value,
                            })
                          }
                          disabled={!employeeId}
                        />
                      </div>
                      <div className="col-md-2">
                        <label className="form-label">
                          End Date<span className="text-danger">*</span>
                        </label>
                        <input
                          type="date"
                          className="form-control"
                          value={leaveForm.endDate}
                          onChange={(e) =>
                            setLeaveForm({
                              ...leaveForm,
                              endDate: e.target.value,
                            })
                          }
                          disabled={!employeeId}
                        />
                      </div>
                      <div className="col-md-2">
                        <label className="form-label">Status</label>
                        <select
                          className="form-control"
                          value={leaveForm.status}
                          onChange={(e) =>
                            setLeaveForm({
                              ...leaveForm,
                              status: e.target.value,
                            })
                          }
                          disabled={!employeeId}
                        >
                          <option value="Pending">Pending</option>
                          <option value="Approved">Approved</option>
                          <option value="Rejected">Rejected</option>
                        </select>
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Approver Name</label>
                        <input
                          className="form-control"
                          value={leaveForm.approverName}
                          onChange={(e) =>
                            setLeaveForm({
                              ...leaveForm,
                              approverName: e.target.value,
                            })
                          }
                          disabled={!employeeId}
                          placeholder="Manager name"
                        />
                      </div>
                      <div className="col-12">
                        <label className="form-label">Reason</label>
                        <textarea
                          className="form-control"
                          rows="2"
                          value={leaveForm.reason}
                          onChange={(e) =>
                            setLeaveForm({
                              ...leaveForm,
                              reason: e.target.value,
                            })
                          }
                          disabled={!employeeId}
                          placeholder="Reason for leave..."
                        />
                      </div>
                      <div className="col-12">
                        <button
                          className="btn btn-success"
                          onClick={handleApplyLeave}
                          disabled={!employeeId || saving}
                        >
                          <i className="fas fa-check me-1"></i>
                          {saving ? "Saving..." : "Apply Leave"}
                        </button>
                      </div>
                    </div>

                    <hr />
                    <h5 className="mb-3 fs-9">Leave History</h5>
                    {leaves.length === 0 ? (
                      <div className="alert alert-info">
                        No leave records found
                      </div>
                    ) : (
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: "#f3f4f6" }}>
                              <TableCell>Ref #</TableCell>
                              <TableCell>Leave Type</TableCell>
                              <TableCell>Start Date</TableCell>
                              <TableCell>End Date</TableCell>
                              <TableCell align="center">Days</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Approver</TableCell>
                              <TableCell>Reason</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {paginateData(leaves, leavePage).map((leave) => (
                              <TableRow key={leave.id} hover>
                                <TableCell>
                                  <code>
                                    {leave.referenceNumber ||
                                      leave.ref ||
                                      "N/A"}
                                  </code>
                                </TableCell>
                                <TableCell>
                                  {leave.leaveTypeName || leave.type || "N/A"}
                                </TableCell>
                                <TableCell>
                                  {leave.fromDate
                                    ? new Date(
                                        leave.fromDate,
                                      ).toLocaleDateString()
                                    : leave.from
                                      ? new Date(
                                          leave.from,
                                        ).toLocaleDateString()
                                      : "N/A"}
                                </TableCell>
                                <TableCell>
                                  {leave.toDate
                                    ? new Date(
                                        leave.toDate,
                                      ).toLocaleDateString()
                                    : leave.to
                                      ? new Date(leave.to).toLocaleDateString()
                                      : "N/A"}
                                </TableCell>
                                <TableCell align="center">
                                  <span className="badge bg-info">
                                    {leave.totalDays || leave.days || 0}
                                  </span>
                                </TableCell>
                                <TableCell>
                                  <span
                                    className={`badge ${
                                      leave.status === "Approved"
                                        ? "bg-success"
                                        : leave.status === "Rejected"
                                          ? "bg-danger"
                                          : "bg-warning"
                                    }`}
                                  >
                                    {leave.status || "Pending"}
                                  </span>
                                </TableCell>
                                <TableCell>
                                  {leave.approverName || "-"}
                                </TableCell>
                                <TableCell>
                                  <small>{leave.reason || "-"}</small>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                    {leaves.length > itemsPerPage && (
                      <div className="d-flex justify-content-center mt-3">
                        <Pagination
                          count={Math.ceil(leaves.length / itemsPerPage)}
                          page={leavePage}
                          onChange={(e, page) => setLeavePage(page)}
                          color="primary"
                          size="small"
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Loans Tab */}
                {activeTab === "loans" && (
                  <div>
                    <h5 className="mb-3 fs-9 border-bottom pb-2">Apply Loan</h5>

                    {!employeeId && (
                      <div className="alert alert-warning mb-3">
                        <i className="fas fa-info-circle me-2"></i>
                        Please save personal information first
                      </div>
                    )}

                    <div className="row g-3 mb-4">
                      <div className="col-md-3">
                        <label className="form-label">
                          Loan Type<span className="text-danger">*</span>
                        </label>
                        <select
                          className="form-control"
                          value={loanForm.loanTypeId}
                          onChange={(e) =>
                            setLoanForm({
                              ...loanForm,
                              loanTypeId: e.target.value,
                            })
                          }
                          disabled={!employeeId}
                        >
                          <option value="">-- Select Type --</option>
                          {loanTypes.map((lt) => (
                            <option key={lt.id} value={lt.id}>
                              {lt.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-2">
                        <label className="form-label">
                          Principal Amount<span className="text-danger">*</span>
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={loanForm.principalAmount}
                          onChange={(e) =>
                            setLoanForm({
                              ...loanForm,
                              principalAmount: e.target.value,
                            })
                          }
                          disabled={!employeeId}
                        />
                      </div>
                      <div className="col-md-2">
                        <label className="form-label">Tenure (Months)</label>
                        <input
                          type="number"
                          className="form-control"
                          value={loanForm.tenureMonths}
                          onChange={(e) =>
                            setLoanForm({
                              ...loanForm,
                              tenureMonths: e.target.value,
                            })
                          }
                          disabled={!employeeId}
                        />
                      </div>
                      <div className="col-md-2">
                        <label className="form-label">
                          Interest % (Annual)
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={loanForm.interestRate}
                          onChange={(e) =>
                            setLoanForm({
                              ...loanForm,
                              interestRate: e.target.value,
                            })
                          }
                          disabled={!employeeId}
                          step="0.1"
                        />
                      </div>
                      <div className="col-md-2">
                        <label className="form-label">Start Month</label>
                        <input
                          type="month"
                          className="form-control"
                          value={loanForm.startMonth}
                          onChange={(e) =>
                            setLoanForm({
                              ...loanForm,
                              startMonth: e.target.value,
                            })
                          }
                          disabled={!employeeId}
                        />
                      </div>
                      <div className="col-md-1">
                        <label className="form-label">Installment Day</label>
                        <input
                          type="number"
                          className="form-control"
                          value={loanForm.installmentDay}
                          onChange={(e) =>
                            setLoanForm({
                              ...loanForm,
                              installmentDay: e.target.value,
                            })
                          }
                          disabled={!employeeId}
                          min="1"
                          max="28"
                        />
                      </div>
                      <div className="col-12">
                        <label className="form-label">Purpose</label>
                        <textarea
                          className="form-control"
                          rows="2"
                          value={loanForm.purpose}
                          onChange={(e) =>
                            setLoanForm({
                              ...loanForm,
                              purpose: e.target.value,
                            })
                          }
                          disabled={!employeeId}
                        />
                      </div>
                      <div className="col-12">
                        <button
                          className="btn btn-success"
                          onClick={handleApplyLoan}
                          disabled={!employeeId || saving}
                        >
                          <i className="fas fa-check me-1"></i>
                          {saving ? "Saving..." : "Apply Loan"}
                        </button>
                      </div>
                    </div>

                    <hr />
                    <h5 className="mb-3 fs-9">Loan History</h5>
                    {loans.length === 0 ? (
                      <div className="alert alert-info">
                        No loan records found
                      </div>
                    ) : (
                      <>
                        <TableContainer
                          component={Paper}
                          variant="outlined"
                          className="mb-4"
                        >
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ bgcolor: "#f3f4f6" }}>
                                <TableCell>Ref #</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Start</TableCell>
                                <TableCell align="right">Principal</TableCell>
                                <TableCell align="center">Tenure</TableCell>
                                <TableCell align="right">Rate %</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="center">Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {paginateData(loans, loanPage).map((loan) => (
                                <TableRow key={loan.id} hover>
                                  <TableCell>
                                    <code>
                                      {loan.referenceNumber ||
                                        loan.ref ||
                                        "N/A"}
                                    </code>
                                  </TableCell>
                                  <TableCell>
                                    {loan.loanTypeName || loan.type || "N/A"}
                                  </TableCell>
                                  <TableCell>
                                    {loan.startDate
                                      ? new Date(
                                          loan.startDate,
                                        ).toLocaleDateString()
                                      : "N/A"}
                                  </TableCell>
                                  <TableCell align="right">
                                    Rs.{" "}
                                    {(
                                      loan.principalAmount ||
                                      loan.principal ||
                                      0
                                    ).toLocaleString()}
                                  </TableCell>
                                  <TableCell align="center">
                                    {loan.tenureMonths || loan.tenure || 0}{" "}
                                    months
                                  </TableCell>
                                  <TableCell align="right">
                                    {loan.interestRate || 0}%
                                  </TableCell>
                                  <TableCell>
                                    <span className="badge bg-warning">
                                      {loan.status || "Pending"}
                                    </span>
                                  </TableCell>
                                  <TableCell align="center">
                                    <button
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={() =>
                                        setExpandedLoan(
                                          expandedLoan === loan.id
                                            ? null
                                            : loan.id,
                                        )
                                      }
                                    >
                                      <i
                                        className={`fas fa-${expandedLoan === loan.id ? "minus" : "plus"}-circle`}
                                      ></i>
                                    </button>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>

                        {/* Installment Plan for Expanded Loan */}
                        {expandedLoan &&
                          loans.find((l) => l.id === expandedLoan)
                            ?.installments && (
                            <div className="card mb-3">
                              <div className="card-header bg-light">
                                <h6 className="mb-0">
                                  <i className="fas fa-calendar-alt me-2"></i>
                                  Installment Plan -{" "}
                                  {
                                    loans.find((l) => l.id === expandedLoan)
                                      ?.referenceNumber
                                  }
                                </h6>
                              </div>
                              <div className="card-body p-0">
                                <TableContainer>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow sx={{ bgcolor: "#f8f9fa" }}>
                                        <TableCell align="center">#</TableCell>
                                        <TableCell>Due Date</TableCell>
                                        <TableCell align="right">
                                          Opening
                                        </TableCell>
                                        <TableCell align="right">
                                          Principal
                                        </TableCell>
                                        <TableCell align="right">
                                          Interest
                                        </TableCell>
                                        <TableCell align="right">
                                          Installment
                                        </TableCell>
                                        <TableCell align="right">
                                          Closing
                                        </TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {loans
                                        .find((l) => l.id === expandedLoan)
                                        ?.installments?.map((inst) => (
                                          <TableRow
                                            key={inst.installmentNumber}
                                            hover
                                          >
                                            <TableCell align="center">
                                              <strong>
                                                {inst.installmentNumber}
                                              </strong>
                                            </TableCell>
                                            <TableCell>
                                              {new Date(
                                                inst.dueDate,
                                              ).toLocaleDateString()}
                                            </TableCell>
                                            <TableCell align="right">
                                              Rs.{" "}
                                              {inst.openingBalance.toLocaleString()}
                                            </TableCell>
                                            <TableCell align="right">
                                              Rs.{" "}
                                              {inst.principalAmount.toLocaleString()}
                                            </TableCell>
                                            <TableCell
                                              align="right"
                                              className="text-danger"
                                            >
                                              Rs.{" "}
                                              {inst.interestAmount.toLocaleString()}
                                            </TableCell>
                                            <TableCell align="right">
                                              <strong>
                                                Rs.{" "}
                                                {inst.installmentAmount.toLocaleString()}
                                              </strong>
                                            </TableCell>
                                            <TableCell align="right">
                                              Rs.{" "}
                                              {inst.closingBalance.toLocaleString()}
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </div>
                            </div>
                          )}
                      </>
                    )}
                    {loans.length > itemsPerPage && (
                      <div className="d-flex justify-content-center mt-3">
                        <Pagination
                          count={Math.ceil(loans.length / itemsPerPage)}
                          page={loanPage}
                          onChange={(e, page) => setLoanPage(page)}
                          color="primary"
                          size="small"
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Employment History Tab */}
                {activeTab === "history" && (
                  <div>
                    <h5 className="mb-3 fs-9 border-bottom pb-2">
                      Employment History
                    </h5>

                    {employmentHistory.length === 0 ? (
                      <div className="alert alert-info">
                        No employment history found
                      </div>
                    ) : (
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: "#f3f4f6" }}>
                              <TableCell>Department</TableCell>
                              <TableCell>Designation</TableCell>
                              <TableCell>Effective From</TableCell>
                              <TableCell>Effective To</TableCell>
                              <TableCell>Remarks</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {employmentHistory.map((history) => (
                              <TableRow key={history.id} hover>
                                <TableCell>
                                  {history.departmentName || "N/A"}
                                </TableCell>
                                <TableCell>
                                  {history.designationTitle || "N/A"}
                                </TableCell>
                                <TableCell>
                                  {history.effectiveFrom
                                    ? history.effectiveFrom.split("T")[0]
                                    : "N/A"}
                                </TableCell>
                                <TableCell>
                                  {history.effectiveTo
                                    ? history.effectiveTo.split("T")[0]
                                    : "Current"}
                                </TableCell>
                                <TableCell>{history.reason || "-"}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmployeeProfile;
